import { useState } from 'react';
import { RolesSection } from '../manage/RolesSection.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { toastError } from '../../components/ui/Toast.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, StatusBadge, CardHead } from '../../components/ui/Primitives.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { useSession } from '../../lib/auth/session.js';
import { relativeTime } from '../../lib/format.js';

/**
 * Everyone who can get into the platform.
 *
 * The same rows as a tenant's own Portal users tab, without the tenant fixed --
 * ADR-019 says that tab is the tenant-scoped version of THIS page, so the two
 * read from one endpoint shape rather than diverging into two ideas of what a
 * user is.
 *
 * Pending invitations sit in the same list, not on a separate screen. Somebody
 * invited and not yet accepted holds a live token: they can get in, so they
 * belong in the answer to "who can get in". Splitting them is how an unrevoked
 * invitation to a departed employee survives an access review.
 */

interface Row {
  user_id: string;
  email: string;
  full_name: string | null;
  status: string;
  last_seen_at: string | null;
  membership_id: string;
  scope_level: 'platform' | 'reseller' | 'tenant' | 'site';
  role_key: string;
  role_name: string;
  tenant_id: string | null;
  tenant_name: string | null;
  reseller_name: string | null;
  granted_at: string;
  /** Two-step verification is on (docs/06 §2.1). */
  mfa_enabled?: boolean;
}
interface Pending {
  id: string; email: string; full_name: string | null; scope_level: string;
  role_name: string; tenant_name: string | null;
  expires_at: string; created_at: string;
}

const SCOPE_TONE = {
  platform: 'danger', reseller: 'warn', tenant: 'muted', site: 'muted',
} as const;

export function PlatformUsersPage() {
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  const [scope, setScope] = useState('all');
  const [search, setSearch] = useState('');

  const key = ['platform-users', scope, search] as const;
  const users = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ data: Row[]; pending: Pending[] }>(
      `/v1/users?${new URLSearchParams({
        scope, ...(search ? { search } : {}),
      })}`),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.post(`/v1/invitations/${id}/actions/revoke`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['platform-users'] }),
  });
  // For a lost phone with no recovery codes left: audited, and the admin confirms it is them.
  const me = useSession((s) => s.session?.user.id);
  const step = useStepUp();
  const resetMfa = useMutation({
    mutationFn: (userId: string) => api.delete<void>(`/v1/users/${userId}/mfa`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['platform-users'] }),
  });

  if (users.error) return <ErrorBanner error={users.error} />;

  const rows = users.data?.data ?? [];
  const pending = users.data?.pending ?? [];
  const platformStaff = rows.filter((r) => r.scope_level === 'platform').length;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Portal users</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            {rows.length} membership{rows.length === 1 ? '' : 's'}
            {platformStaff > 0 && <> · {platformStaff} with platform access</>}
            {pending.length > 0 && <> · {pending.length} invited, not yet accepted</>}
          </p>
        </div>
      </div>

      {/*
        Platform access is the short, consequential list, so it is called out
        rather than left to be counted off a table. Anyone here can reach every
        customer on the system. A hint, not a banner: it is the normal state, not
        a problem.
      */}
      {platformStaff > 0 && (
        <p className="arc-note">
          {platformStaff} {platformStaff === 1 ? 'person has' : 'people have'} platform
          access — every customer, every invoice, every trunk. Worth reviewing
          when somebody leaves.
        </p>
      )}

      <div className="arc-toolbar" style={{ marginBottom: 12 }}>
        <select className="arc-select" style={{ width: 'auto' }} value={scope}
          onChange={(e) => setScope(e.target.value)} aria-label="Filter by scope">
          <option value="all">Everyone</option>
          <option value="platform">Platform staff</option>
          <option value="reseller">Reseller staff</option>
          <option value="tenant">Customer users</option>
        </select>
        <input className="arc-input" style={{ maxWidth: 260 }}
          placeholder="Search name or email" value={search}
          onChange={(e) => setSearch(e.target.value)} />
        <div className="arc-spacer" />
        <span className="arc-subtle">{rows.length} shown</span>
      </div>

      {pending.length > 0 && (
        <div className="arc-card" style={{ marginBottom: 12 }}>
          <CardHead title="Invited, not yet accepted" sub="These tokens work until revoked or expired." />
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>Email</th><th>Role</th><th>Scope</th>
                  <th>Expires</th>{can('user:invite') && <th style={{ width: 90 }} />}
                </tr>
              </thead>
              <tbody>
                {pending.map((p) => {
                  const expired = new Date(p.expires_at).getTime() < Date.now();
                  return (
                    <tr key={p.id}>
                      <td>
                        <div style={{ fontWeight: 550 }}>{p.email}</div>
                        {p.full_name && (
                          <div className="arc-subtle" style={{ marginTop: 2 }}>{p.full_name}</div>
                        )}
                      </td>
                      <td>{p.role_name}</td>
                      <td>
                        <Badge tone={SCOPE_TONE[p.scope_level as keyof typeof SCOPE_TONE] ?? 'muted'}>
                          {p.tenant_name ?? p.scope_level}
                        </Badge>
                      </td>
                      <td className="arc-subtle">
                        {expired
                          ? <Badge tone="muted">expired</Badge>
                          : relativeTime(p.expires_at)}
                      </td>
                      {can('user:invite') && (
                        <td>
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            disabled={revoke.isPending}
                            title="Revoke this invitation so the link stops working"
                            onClick={() => revoke.mutate(p.id)}>
                            Revoke
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <StepUpPrompt step={step} what="Resetting two-step verification" />
      {resetMfa.error && <ErrorBanner error={resetMfa.error} />}
      <div className="arc-card">
        {users.data === undefined ? (
          <TableSkeleton rows={6} cols={5} />
        ) : rows.length === 0 ? (
          <Empty icon="users" title="Nobody matches"
            {...(search ? { hint: 'No user with that name or email.' } : {})} />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>User</th><th>Role</th><th>Access to</th>
                  <th>Status</th><th>Last seen</th><th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.membership_id}>
                    <td>
                      <div style={{ fontWeight: 550 }}>{r.full_name ?? r.email}</div>
                      {r.full_name && (
                        <div className="arc-subtle" style={{ marginTop: 2 }}>{r.email}</div>
                      )}
                    </td>
                    <td>{r.role_name}</td>
                    <td>
                      {/*
                        What they can reach, not the scope word. "Platform" and
                        a customer's name answer different questions and the
                        name is the one being asked.
                      */}
                      <Badge tone={SCOPE_TONE[r.scope_level] ?? 'muted'}>
                        {r.scope_level === 'platform' ? 'the whole platform'
                          : r.tenant_name ?? r.reseller_name ?? r.scope_level}
                      </Badge>
                    </td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="arc-subtle">
                      {r.last_seen_at ? relativeTime(r.last_seen_at) : 'never signed in'}
                    </td>
                    <td className="arc-td-right">
                      {r.mfa_enabled && can('user:write') && r.user_id !== me && (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={resetMfa.isPending}
                          onClick={() => {
                            void confirmAction({
                              title: `Turn off two-step verification for ${r.email}?`,
                              body: 'They sign in with their password alone until they set it up again.',
                              action: 'Turn off', danger: true,
                            }).then((ok) => {
                              if (ok) step.run(() => resetMfa.mutateAsync(r.user_id)).catch(toastError);
                            });
                          }}>
                          Reset 2FA
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="arc-hint" style={{ marginTop: 12 }}>
        A person with memberships on several accounts appears once per
        membership — that is what they hold, and collapsing them would hide an
        access grant.
      </p>

      {/* The platform's own roles, for platform staff, resellers' staff or client accounts. */}
      {can('role:read') && <RolesSection owner={{ kind: 'platform' }} />}
    </>
  );
}
